import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { shareReplay, tap } from 'rxjs/operators';

export interface BlogPost {
  id?: number;
  title: string;
  slug?: string;
  description?: string;
  content: string;
  coverImage?: string;
  coverImageSeoTitle?: string;
  coverImageAlt?: string;
  coverImageCaption?: string;
  status?: 'PENDING' | 'APPROVED' | 'REJECTED';
  category?: string;
  metaTitle?: string;
  metaDescription?: string;
  linkedinLink?: string;
  showInFooter?: boolean;
  author?: { 
    name: string; 
    email?: string;
    image?: string;
    designation?: string;
    description?: string;
  };
  authorId?: number;
  reviewedBy?: { name: string; email?: string };
  createdAt?: string;
  updatedAt?: string;
}

import { environment } from 'src/environments/environment';

@Injectable({ providedIn: 'root' })
export class BlogService {
  private apiUrl = `${environment.apiUrl}/blogs`;

  constructor(private http: HttpClient) { }

  private publicBlogsCache$?: Observable<BlogPost[]>;
  private allBlogsCache$?: Observable<BlogPost[]>;

  getPublicBlogs(): Observable<BlogPost[]> {
    if (!this.publicBlogsCache$) {
      this.publicBlogsCache$ = this.http.get<BlogPost[]>(`${this.apiUrl}/public`).pipe(
        shareReplay(1)
      );
    }
    return this.publicBlogsCache$;
  }

  getAllBlogs(): Observable<BlogPost[]> {
    if (!this.allBlogsCache$) {
      this.allBlogsCache$ = this.http.get<BlogPost[]>(this.apiUrl).pipe(
        shareReplay(1)
      );
    }
    return this.allBlogsCache$;
  }

  private clearCache() {
    this.publicBlogsCache$ = undefined;
    this.allBlogsCache$ = undefined;
  }

  getMyBlogs(): Observable<BlogPost[]> {
    return this.http.get<BlogPost[]>(`${this.apiUrl}/my-blogs`);
  }

  createBlog(blog: { title: string; content: string }): Observable<BlogPost> {
    return this.http.post<BlogPost>(this.apiUrl, blog).pipe(tap(() => this.clearCache()));
  }

  updateBlog(id: number, blog: { title: string; content: string }): Observable<BlogPost> {
    return this.http.put<BlogPost>(`${this.apiUrl}/${id}`, blog).pipe(tap(() => this.clearCache()));
  }

  reviewBlog(id: number, status: 'APPROVED' | 'REJECTED'): Observable<BlogPost> {
    return this.http.put<BlogPost>(`${this.apiUrl}/${id}/review`, { status }).pipe(tap(() => this.clearCache()));
  }

  deleteBlog(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`).pipe(tap(() => this.clearCache()));
  }
}
